import React from "react";
import styled from "styled-components";
import { Box, Button, Text } from "@origin-digital/ods-core";
import { IconCheck } from "@origin-digital/ods-icons";
import type { ISearchTabDto } from "../../contract/generated/bff/src/models";

const SelectedPill = styled(Button)`
  &.MuiButton-contained {
    background-color: ${(props) => props.theme.colors.neutralDark}
  };
    padding: 8px 16px 8px 16px;
    
    &:hover {
      background-color: #3d3d3d;
    }
  }
`;

const OutlinedPill = styled(Button)`
  &.MuiButton-outlined {
    border-color: #ababab;
  }
`;

interface FilterChipsProps {
  tabs: ISearchTabDto[];
  selectedTab: ISearchTabDto | null;
  onSelect: (tab: ISearchTabDto | null) => void;
}

const chipId = (tab: ISearchTabDto) =>
  tab.filter ?? tab.label.toLowerCase().replace(/\s+/g, "-");

const FilterChip: React.FC<{
  tab: ISearchTabDto;
  selected: boolean;
  onSelect: (tab: ISearchTabDto) => void;
}> = ({ tab, selected, onSelect }) => {
  const id = chipId(tab);

  if (selected) {
    return (
      <SelectedPill
        size="xsmall"
        variant="contained"
        color="secondaryB"
        icon={<IconCheck color="white" />}
        onClick={() => onSelect(tab)}
        data-id={`filter-chip-${id}`}
      >
        <Text variant="body-small" weight="medium" tone="white">
          {tab.label}
        </Text>
      </SelectedPill>
    );
  }

  return (
    <OutlinedPill
      size="xsmall"
      variant="outlined"
      color="secondaryB"
      onClick={() => onSelect(tab)}
      data-id={`filter-chip-${id}`}
    >
      <Text variant="body-small" weight="medium" tone="neutral">
        {tab.label}
      </Text>
    </OutlinedPill>
  );
};

export const FilterChips: React.FC<FilterChipsProps> = ({
  tabs,
  selectedTab,
  onSelect,
}) => {
  if (!tabs.length) return null;

  const isTabSelected = (tab: ISearchTabDto) =>
    selectedTab !== null &&
    (tab.filter != null
      ? tab.filter === selectedTab.filter
      : tab.label === selectedTab.label);

  return (
    <Box
      display="flex"
      flexDirection="row"
      gap="small"
      data-id="search-filter-chips"
      style={{ flexWrap: "wrap" }}
    >
      {tabs.map((tab) => (
        <FilterChip
          key={chipId(tab)}
          tab={tab}
          selected={isTabSelected(tab)}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};
